import { dateToEpochSeconds, epochSecondsToDate, stripUndefined, z, type ZodType } from '@customerio/hydra-validation';
import type { Layout as UIAPILayout, LayoutStarter as UIAPILayoutStarter } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Layout entity — workspace-scoped reusable email layouts.
 *
 * The wire shape uses numeric ids and epoch-second timestamps. The db
 * layer normalizes ids to strings and timestamps to Date instances so
 * cache keys and consumers follow the same conventions as snippets,
 * tags, and subscription topics.
 */

export type Layout = Omit<UIAPILayout, 'id' | 'created' | 'updated' | 'starter_layout_id'> & {
  id: string;
  created: Date;
  updated: Date;
  starter_layout_id: string | null;
};

export type LayoutStarter = Omit<UIAPILayoutStarter, 'id'> & {
  id: string;
};

type LayoutWriteInput = Partial<Layout>;
type LayoutWireWritePayload = Partial<UIAPILayout>;

const LayoutFromApi = z.looseObject({
  id: z.number().transform(String),
  created: epochSecondsToDate,
  updated: epochSecondsToDate,
  starter_layout_id: z
    .number()
    .nullish()
    .transform((id) => (id == null ? null : String(id))),
}) as unknown as ZodType<Layout, unknown>;

const LayoutToApi = z
  .looseObject({
    id: z.string().transform(Number).pipe(z.number().finite()).optional(),
    created: dateToEpochSeconds.optional(),
    updated: dateToEpochSeconds.optional(),
    starter_layout_id: z.string().transform(Number).pipe(z.number().finite()).nullable().optional(),
  })
  .transform(stripUndefined) as unknown as ZodType<LayoutWireWritePayload, LayoutWriteInput>;

const LayoutStarterFromApi = z.looseObject({
  id: z.number().transform(String),
}) as unknown as ZodType<LayoutStarter, unknown>;

export interface LayoutListParams {
  archived?: boolean;
}

/** @public */
export const layout = defineEntity<Layout, LayoutWireWritePayload, LayoutListParams, undefined, 'workspace'>({
  name: 'layout',
  scope: 'workspace',
  pk: (l) => l.id,
  schema: { read: LayoutFromApi, write: LayoutToApi },
});

export const layoutKeys = {
  ...layout.keys,
  starters: (workspaceId: number) => [...layout.keys.all(workspaceId), 'starters'] as const,
  actionsUsage: (workspaceId: number, layoutId: string) =>
    [...layout.keys.detail(workspaceId, layoutId), 'usage', 'actions'] as const,
  newslettersUsage: (workspaceId: number, layoutId: string) =>
    [...layout.keys.detail(workspaceId, layoutId), 'usage', 'newsletters'] as const,
  transactionalMessagesUsage: (workspaceId: number, layoutId: string) =>
    [...layout.keys.detail(workspaceId, layoutId), 'usage', 'transactional-messages'] as const,
};

export function parseLayout(raw: UIAPILayout): Layout {
  return layout.parse(raw);
}

export function parseLayoutStarter(raw: UIAPILayoutStarter): LayoutStarter {
  return LayoutStarterFromApi.parse(raw);
}
